/**
 * Whether the point screens may offer to activate or deactivate a location, and what to say
 * before doing it.
 *
 * Kept out of the component so the rules can be read and tested on their own. The server is the
 * real gate -- OrderApi refuses the same clients regardless of what the browser sends -- and this
 * exists so a user is not handed a button that only ever produces a refusal.
 */

/**
 * Clients whose points Railcrew does not own.
 *
 * UP (2) and BNSF (46) send their own locations, and geo.CreateClientPoint carries find-or-create
 * matching for exactly those two, so their rows are the feed's to activate and retire. CSX (11)
 * stopped being a client in August 2026: settled trips and invoices still reference their points,
 * so the rows stay, but nothing about them changes any more.
 *
 * The same list AddPoint uses to decide which clients a new point may be filed under, for the
 * same reason. OrderApi refuses these ids on the way in; this only keeps the screen honest.
 */
export const FEED_MANAGED_CLIENT_IDS = [2, 46, 11];

export const isFeedManagedClient = (clientId?: number): boolean =>
  typeof clientId === "number" && FEED_MANAGED_CLIENT_IDS.includes(clientId);

export interface PointToggleGuard {
  /** Whether the control may be used at all. */
  allowed: boolean;
  /** Empty when allowed. Otherwise the sentence shown beside the disabled control. */
  reason: string;
}

/**
 * Whether this point's own active flag may be changed from the screen.
 *
 * A missing client id is refused rather than allowed: the call is client-scoped and OrderApi
 * checks the id against the row, so sending nothing would fail anyway -- and it fails more
 * usefully here, where there is room to say why.
 */
export const pointToggleGuard = (clientId?: number): PointToggleGuard => {
  if (typeof clientId !== "number") {
    return {
      allowed: false,
      reason: "This location has no client on it, so it cannot be activated or deactivated here.",
    };
  }

  if (isFeedManagedClient(clientId)) {
    return {
      allowed: false,
      reason:
        "This client's locations come from their own feed, so they are not activated or " +
        "deactivated in Flow. Ask the railroad to retire the location on their side.",
    };
  }

  return { allowed: true, reason: "" };
};

/**
 * Whether a hub link actually works, as opposed to whether its own flag is on.
 *
 * geo.PointOfService.IsActive is computed as ClientPointToHub.isActive AND ClientPoint.IsActive,
 * so a link switched on under a deactivated point is still invisible everywhere. Reporting the
 * link's own flag as "Active" in that state would rebuild the exact complaint this screen was
 * built to end.
 */
export const isHubEffectivelyActive = (hubIsActive?: boolean, pointIsActive?: boolean): boolean =>
  !!hubIsActive && !!pointIsActive;

/**
 * The sentence a hub row needs when its own flag disagrees with what the point of service does.
 *
 * Empty whenever the two agree, which is the ordinary case -- there is nothing to explain when
 * the switch and the outcome match.
 */
export const hubStateNote = (hubIsActive?: boolean, pointIsActive?: boolean): string => {
  if (!hubIsActive || pointIsActive) return "";

  return (
    "The hub link is on, but the location itself is deactivated, so this point of service is " +
    "still unusable. Activate the location first."
  );
};

/**
 * What deactivating this location will do, in the words shown in the confirmation.
 *
 * Written as consequences rather than as a question about a flag -- "are you sure?" tells a
 * dispatcher nothing about what breaks.
 *
 * The hub count is named because deactivating the point takes every one of its points of service
 * down with it, without any of their own flags changing. Someone who has just been switching
 * individual links on will not expect that from a control sitting on the other card.
 */
export const pointDeactivationWarning = (pointName?: string, hubCount = 0): string => {
  const name = pointName?.trim() || "This location";
  const hubs =
    hubCount > 0
      ? ` All ${hubCount} of its hub assignment${hubCount === 1 ? "" : "s"} become unusable at the ` +
        "same time, even though their own switches stay as they are."
      : "";

  return (
    `${name} will stop being offered anywhere a location is picked -- on new orders and when a ` +
    `trip is closed.${hubs} Trips and invoices that already reference it are untouched, and it ` +
    "can be activated again from this screen."
  );
};

/**
 * What deactivating one hub link will do. Narrower than deactivating the point: the location
 * stays live everywhere else, it just stops being reachable from this one hub.
 */
export const hubDeactivationWarning = (hubName?: string): string => {
  const hub = hubName?.trim() || "this hub";
  return (
    `This location will stop being selectable from ${hub}. Any other hub serving it is ` +
    "unaffected, trips that already used this point of service are untouched, and the link can " +
    "be switched back on here."
  );
};
